/**
 * LandingArchetypes — eight freelance personas with their core jobs-to-be-done.
 *
 * Replaces fake testimonials. Each archetype is a "Para tú que…" card
 * that lets the visitor self-identify and immediately see the 3 features
 * that matter most to their workflow. No invented quotes, no stock
 * pictures — editorial, clean, scannable.
 *
 * Each card carries a "Conoce a fondo cómo Lumfi te ayuda" link to a
 * dedicated /para/:slug deep-dive page powered by
 * /src/data/landingArchetypeContent.js.
 *
 * The 8 archetypes were curated to cover the LATAM freelance landscape:
 *   1. Software developer billing in USD / paid in COP/MXN/ARS
 *   2. UX/UI designer with multi-month fixed-price projects
 *   3. Content creator with ad + brand revenue
 *   4. Independent consultant on retainer + spot work
 *   5. Lawyer / accountant with cyclical receivables
 *   6. Online tutor / coach with subscription income
 *   7. E-commerce solo seller (Mercado Libre / Shopify)
 *   8. Wedding / event photographer with seasonal peaks
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Code2, Palette, Video, Briefcase, Scale, GraduationCap,
  ShoppingBag, Camera, ArrowRight, ArrowUpRight,
} from "lucide-react";

const ICONS = {
  dev: Code2, design: Palette, content: Video, consult: Briefcase,
  legal: Scale, coach: GraduationCap, ecom: ShoppingBag, photo: Camera,
};

// Slug map → matches the dynamic /for/:slug route in App.js. Kept here
// (vs. importing from the data file) so this presentational component
// stays decoupled from the deep-dive content layer.
const SLUGS = {
  dev: "developer", design: "designer", content: "content-creator",
  consult: "consultant", legal: "lawyer-accountant", coach: "coach-tutor",
  ecom: "ecommerce", photo: "photographer",
};

const ARCHETYPES = {
  es: [
    {
      key: "dev",
      who: "Desarrollador/a freelance",
      copy: "Cobras en USD a clientes del exterior, vives en moneda local. Quieres saber el cash real sin tener que abrir 3 apps.",
      features: ["Conversión FX automática", "Cobros con probabilidad", "Bot Telegram para registrar entradas"],
      tag: "Cross-currency",
    },
    {
      key: "design",
      who: "Diseñador/a UX/UI",
      copy: "Trabajas por proyectos cortos. Recibes un anticipo, intermedios y un cierre. El mes que cobras te sientes rico, al siguiente te ahogas.",
      features: ["Cash flow semanal", "Anticipos como cobros recurrentes", "Alertas de límite seguro"],
      tag: "Proyectos largos",
    },
    {
      key: "content",
      who: "Content creator",
      copy: "Mezclas AdSense, marcas, suscripciones y eventos. Cada fuente paga distinto día. Quieres un solo número que te diga si vas bien.",
      features: ["Múltiples fuentes de ingreso", "Calendario de cobros", "Resumen semanal por WhatsApp"],
      tag: "Multi-fuente",
    },
    {
      key: "consult",
      who: "Consultor/a independiente",
      copy: "Tienes un retainer mensual + proyectos extra. Necesitas saber qué pasa si te dejan el retainer el mes próximo.",
      features: ["Escenarios what-if", "Ingresos recurrentes vs spot", "Reportes para enviar al contador"],
      tag: "Retainer + spot",
    },
    {
      key: "legal",
      who: "Abogado/a o contador/a",
      copy: "Tus clientes pagan al cierre del mes o cuando salen los honorarios. Hay meses gordos y meses flacos. No quieres sorpresas este mes.",
      features: ["Cobros estacionales", "Proyección a 3 meses", "Exportes para conciliación"],
      tag: "Cíclico",
    },
    {
      key: "coach",
      who: "Coach o tutor/a online",
      copy: "Vendes mensualidades o paquetes. La gente se da de baja y suma, todo el tiempo. Quieres saber tu MRR real, no el bruto.",
      features: ["MRR proyectado", "Métricas de retención", "Alertas de bajas"],
      tag: "Suscripciones",
    },
    {
      key: "ecom",
      who: "E-commerce solo",
      copy: "Vendes en Mercado Libre, Shopify o redes. Cobras cuando ML libera, pagas proveedores antes. La diferencia de cobro/pago te puede matar.",
      features: ["Cuentas separadas por plataforma", "Pagos comprometidos a futuro", "Cash gap por semana"],
      tag: "Inventory gap",
    },
    {
      key: "photo",
      who: "Fotógrafo/a o eventos",
      copy: "Tu temporada es noviembre-marzo. Tienes que vivir 7 meses con la plata de los otros 5. Lumfi te dice cuánto repartir.",
      features: ["Reparto por meses bajos", "Reserva fiscal automática", "Goals de ahorro estacional"],
      tag: "Estacional",
    },
  ],
  en: [
    { key: "dev", who: "Freelance developer", copy: "Bill in USD, get paid in local currency. Want to know your real cash without juggling 3 apps.", features: ["Automatic FX", "Receivables with probability", "Telegram bot for entries"], tag: "Cross-currency" },
    { key: "design", who: "UX/UI designer", copy: "2-3 month projects. Upfront, milestone, closing payments. Rich one month, drowning the next.", features: ["Weekly cash flow", "Recurring milestone receivables", "Safe-limit alerts"], tag: "Long projects" },
    { key: "content", who: "Content creator", copy: "Ads + brands + subscriptions + events. Each source pays a different day. You want ONE number.", features: ["Multiple income sources", "Receivables calendar", "Weekly WhatsApp digest"], tag: "Multi-source" },
    { key: "consult", who: "Independent consultant", copy: "Monthly retainer + extra projects. Need to know what happens if the retainer ends next month.", features: ["What-if scenarios", "Recurring vs spot income", "Accountant exports"], tag: "Retainer + spot" },
    { key: "legal", who: "Lawyer or accountant", copy: "Clients pay at month-end or when fees clear. Some months are fat, some thin. No surprises in February.", features: ["Seasonal receivables", "3-month forecast", "Reconciliation exports"], tag: "Cyclical" },
    { key: "coach", who: "Online coach or tutor", copy: "Monthly subscriptions or packages. People churn and resubscribe. Want real MRR, not gross.", features: ["Projected MRR", "Retention metrics", "Churn alerts"], tag: "Subscriptions" },
    { key: "ecom", who: "Solo e-commerce", copy: "Sell on ML, Shopify or social. Get paid on release, pay suppliers earlier. Timing gap can kill you.", features: ["Separate accounts per platform", "Future committed payments", "Weekly cash gap"], tag: "Inventory gap" },
    { key: "photo", who: "Photographer / events", copy: "Season is Nov-Mar. Have to live 7 months on the income of 5. Lumfi tells you how much to allocate.", features: ["Off-season allocation", "Auto tax reserve", "Seasonal saving goals"], tag: "Seasonal" },
  ],
  // Portuguese reuses Spanish placeholder copy in this MVP — quick to swap later
  pt: null,
};


export function LandingArchetypes({ lang = "es" }) {
  const set = ARCHETYPES[lang] || ARCHETYPES.es;
  const [activeKey, setActiveKey] = useState(set[0].key);
  const active = set.find((a) => a.key === activeKey) || set[0];
  const isEs = lang === "es";
  const isEn = lang === "en";

  // Auto-rotate the highlighted archetype every 6 seconds for visual
  // life. The user can click any tab to pin it and the autoplay pauses
  // for 30 seconds after interaction.
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) {
      const resume = setTimeout(() => setPaused(false), 30_000);
      return () => clearTimeout(resume);
    }
    const t = setInterval(() => {
      setActiveKey((cur) => {
        const idx = set.findIndex((a) => a.key === cur);
        return set[(idx + 1) % set.length].key;
      });
    }, 6000);
    return () => clearInterval(t);
  }, [paused, set]);

  return (
    <section id="archetypes" className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32" data-testid="landing-archetypes">
      <div className="text-center max-w-3xl mx-auto">
        <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">
          <span className="h-px w-8 bg-emerald-700/40" />
          {isEs ? "Hecho para freelance LATAM" : isEn ? "Built for LATAM freelance" : "Feito para freelance LATAM"}
        </span>
        <h2 className="font-serif-display mt-5 text-4xl lg:text-5xl leading-tight font-medium">
          {isEs ? "¿Cuál eres tu?" : isEn ? "Which one are you?" : "Qual é você?"}
        </h2>
        <p className="mt-4 text-slate-600 max-w-2xl mx-auto leading-relaxed">
          {isEs
            ? "Lumfi se adapta a tu tipo de trabajo. Elige el perfil que más se parezca al tuyo y mira como Lumfi te puede ayudar."
            : isEn
            ? "Lumfi adapts to your kind of work. Pick the profile closest to yours and see which features matter most."
            : "Lumfi se adapta ao seu tipo de trabalho. Escolha o perfil que mais se parece com o seu."}
        </p>
      </div>

      <div className="mt-12 grid lg:grid-cols-[280px_1fr] gap-6 lg:gap-10">
        {/* Persona tab list */}
        <div
          className="lg:max-h-[480px] lg:overflow-y-auto flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible snap-x snap-mandatory scrollbar-none -mx-6 px-6 lg:mx-0 lg:px-0"
          role="tablist"
        >
          {set.map((a) => {
            const Icon = ICONS[a.key];
            const isActive = a.key === activeKey;
            return (
              <button /* design-system: ALLOW legacy-todo */
                key={a.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => { setActiveKey(a.key); setPaused(true); }}
                data-testid={`archetype-tab-${a.key}`}
                className={`group shrink-0 min-w-[200px] lg:min-w-0 flex items-center gap-3 px-4 py-3.5 rounded-2xl text-left snap-start transition-all duration-200 ${
                  isActive
                    ? "bg-slate-900 text-white shadow-[0_18px_40px_-20px_rgba(15,23,42,0.45)]"
                    : "bg-white text-slate-700 border border-cream-300 hover:border-slate-300"
                }`}
              >
                <Icon className={`h-5 w-5 shrink-0 ${isActive ? "text-emerald-400" : "text-emerald-700"}`} strokeWidth={1.6} />
                <span className="text-sm font-semibold leading-tight truncate">{a.who}</span>
                {isActive && <ArrowRight className="h-4 w-4 ml-auto text-emerald-400 shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* Active card */}
        <div
          key={active.key}
          className="lf-fade-in-up bg-slate-900 text-white rounded-3xl p-8 lg:p-12 relative overflow-hidden"
          data-testid={`archetype-card-${active.key}`}
        >
          <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full bg-emerald-500/15 blur-3xl" />
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] font-bold uppercase tracking-[0.18em]">
            {active.tag}
          </span>
          <h3 className="font-serif-display text-3xl lg:text-4xl mt-4 leading-tight font-medium">
            {isEs ? "Para ti que eres " : isEn ? "For you who's a " : "Para você que é "}
            <span className="text-emerald-400">{active.who.toLowerCase()}</span>
          </h3>
          <p className="text-slate-300 text-base lg:text-lg leading-relaxed mt-5 max-w-2xl">
            {active.copy}
          </p>
          <div className="mt-8 grid sm:grid-cols-3 gap-3">
            {active.features.map((f) => (
              <div key={f} className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-slate-200 flex items-start gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                <span className="leading-snug">{f}</span>
              </div>
            ))}
          </div>

          {/* Deep-dive CTA → /para/:slug — gives the user a way to read
              a dedicated landing for their persona without leaving the
              flow. Tested via data-testid for click & nav. */}
          <Link
            to={`/for/${SLUGS[active.key]}`}
            data-testid={`archetype-deep-dive-${active.key}`}
            className="relative mt-8 inline-flex items-center gap-2 text-sm font-semibold text-emerald-300 hover:text-emerald-200 transition-colors group"
          >
            {isEs
              ? "Conoce a fondo cómo Lumfi te ayuda"
              : isEn
              ? "See in depth how Lumfi helps you"
              : "Veja como Lumfi te ajuda"}
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
